import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';

const LEADS_ENDPOINT = process.env.REACT_APP_LEADS_ENDPOINT || '/api/leads';

export const ContactForm = () => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const defaultValues = {
    name: '',
    email: '',
    whatsapp: '',
    company: '',
    notes: '',
    honeypot: '',
    consent: false,
    service: 'not-sure',
    stage: 'diagnosis',
    ecommerce_url: '',
  };

  const contactSchema = z.object({
    name: z.string().min(2, language === 'pt' ? 'Nome é obrigatório' : 'Name is required'),
    email: z.string().email(language === 'pt' ? 'E-mail inválido' : 'Invalid email'),
    whatsapp: z.string().min(8, language === 'pt' ? 'WhatsApp é obrigatório' : 'WhatsApp is required'),
    company: z.string().max(160).optional(),
    notes: z.string().max(1200).optional(),
    honeypot: z.string().optional(),
    consent: z.boolean().refine((val) => val === true, {
      message: language === 'pt' ? 'É necessário aceitar os termos' : 'You must accept the terms',
    }),
    service: z.string().min(1),
    stage: z.string().min(1),
    ecommerce_url: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(LEADS_ENDPOINT, {
        ...data,
        company: data.company?.trim() || '',
        notes: data.notes?.trim() || '',
        language,
      });
      setIsSuccess(true);
      reset(defaultValues);
      toast.success(t.contact.successTitle);
    } catch (error) {
      toast.error(t.contact.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="animate-in fade-in zoom-in py-12 text-center duration-500">
        <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-cyan-500/10">
          <CheckCircle className="h-10 w-10 text-cyan-500" />
        </div>
        <h3 className="mb-2 text-2xl font-bold text-slate-100">{t.contact.successTitle}</h3>
        <p className="text-slate-400">{t.contact.successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <input type="text" autoComplete="off" tabIndex={-1} className="hidden" {...register('honeypot')} />
      <input type="hidden" {...register('service')} />
      <input type="hidden" {...register('stage')} />
      <input type="hidden" {...register('ecommerce_url')} />

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-300">
            {t.contact.nameLabel} *
          </Label>
          <Input
            id="name"
            {...register('name')}
            placeholder={t.contact.namePlaceholder}
            className="h-12 border-slate-700 bg-slate-950 text-slate-100"
          />
          {errors.name && <p className="text-sm text-red-400">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-slate-300">
            {t.contact.companyLabel}
          </Label>
          <Input
            id="company"
            {...register('company')}
            placeholder={t.contact.companyPlaceholder}
            className="h-12 border-slate-700 bg-slate-950 text-slate-100"
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">
            {t.contact.emailLabel} *
          </Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder={t.contact.emailPlaceholder}
            className="h-12 border-slate-700 bg-slate-950 text-slate-100"
          />
          {errors.email && <p className="text-sm text-red-400">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="whatsapp" className="text-slate-300">
            {t.contact.whatsappLabel} *
          </Label>
          <Input
            id="whatsapp"
            {...register('whatsapp')}
            placeholder={t.contact.whatsappPlaceholder}
            className="h-12 border-slate-700 bg-slate-950 text-slate-100"
          />
          {errors.whatsapp && <p className="text-sm text-red-400">{errors.whatsapp.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes" className="text-slate-300">
          {language === 'pt' ? 'Como podemos ajudar?' : 'How can we help?'}
        </Label>
        <Textarea
          id="notes"
          {...register('notes')}
          placeholder={
            language === 'pt'
              ? 'Conte rapidamente o que você precisa.'
              : 'Briefly tell us what you need.'
          }
          className="min-h-[120px] border-slate-700 bg-slate-950 text-slate-100"
        />
        {errors.notes && <p className="text-sm text-red-400">{errors.notes.message}</p>}
      </div>

      <div className="flex items-start gap-3 py-1">
        <Checkbox
          id="consent"
          checked={watch('consent')}
          onCheckedChange={(checked) => setValue('consent', checked === true, { shouldValidate: true })}
          className="mt-1 border-slate-600"
        />
        <div>
          <Label htmlFor="consent" className="cursor-pointer text-sm text-slate-400">
            {t.contact.consentLabel} *
          </Label>
          {errors.consent && <p className="mt-1 text-sm text-red-400">{errors.consent.message}</p>}
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || !isValid}
        className="h-14 w-full rounded-full bg-cyan-500 font-bold text-slate-950 transition-all hover:bg-cyan-400"
      >
        {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t.contact.submitButton}
      </Button>
    </form>
  );
};
